import React, { useLayoutEffect, useEffect } from 'react';
import { useDebounce } from 'react-use';

// styles
import { ThemeProvider } from 'styled-components';

import 'assets/styles/ReactToastify.css';
import 'assets/lib/styles/bootstrap-grid.min.css';
import cleanDeep from 'clean-deep';

// components
import Router from 'routes/Router';

// context
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { EditorProvider } from 'context/EditorContext';
import {
  UserProvider,
  useUserContext,
  updateGatherRedirect,
  actions,
} from 'context/UserContext';
import { AppProvider } from 'context/AppContext';
import { IntlProvider } from 'react-intl';
import { useRouterContext } from 'hooks';
import { toast, ToastContainer, Slide } from 'react-toastify';
import { ToastCloseButton } from 'containers';
import { queryStatus, logout } from 'services/requests';
import { CancelToken, isCancel } from 'axios';
import { deployEnv } from 'services/constants';
import { LastLocationProvider } from 'react-router-last-location';
import { deleteAllDrafts } from 'redux/draftsSlice';

import store from 'redux/store';
import pt from 'data/messages/pt';
import languages from 'data/languages';
import GlobalStyle from './GlobalStyle';
import packageJson from '../package.json';
import theme from './services/theme.semantic';

const { defaultLangKey } = languages;
const messages = { pt };

// Pages where the user info won't be refetched
const refetchUserWhitelist = ['/login', '/gather'];

// used to debug renders
// check https://www.npmjs.com/package/@welldone-software/why-did-you-render
// add Component.whyDidYouRender = true to track re-renders
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const App = () => {
  const [{ isPreviousGatherPage }, dispatch] = useUserContext();
  const { history, location } = useRouterContext();
  const dispatchRedux = useDispatch();
  const { draftsAppVersion } = useSelector(state => state.drafts);

  // handle versions bumps
  // when version changes it deletes drafts localstorage to prevent conflicts
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.table({
      'app version': packageJson.version,
      environment: deployEnv,
    });
    if (draftsAppVersion !== packageJson.version) dispatchRedux(deleteAllDrafts());
  }, [draftsAppVersion]);

  // redirect to /gather to check if the user is authenticated
  useLayoutEffect(() => {
    const url = window.location.pathname + window.location.search;
    updateGatherRedirect(dispatch, url);
    history.push('/gather');
  }, [dispatch, history]);

  // refetch user everytime the route changes
  // todo refactor
  useDebounce(
    () => {
      const { pathname } = location;
      const shouldRefetchUser = !refetchUserWhitelist.includes(pathname);

      // used to prevent double refetch
      // on app load the user is redirected to gather which does the fetching there
      dispatch({
        type: actions.UPDATE_GATHER_PREVIOUS,
        data: false,
      });

      const source = CancelToken.source();
      async function fetchUser() {
        try {
          dispatch({
            type: actions.QUERY_CURRENT_USER_LOADING,
          });
          const { data: currentUserRaw } = await queryStatus({
            cancelToken: source.token,
          });
          const currentUser = cleanDeep(currentUserRaw);
          dispatch({
            type: actions.QUERY_CURRENT_USER_SUCCESS,
            data: {
              ...currentUser,
            },
          });
        } catch (error) {
          const cancelled = isCancel(error);
          if (!cancelled) {
            logout();
            dispatch({
              type: actions.QUERY_CURRENT_USER_FAILURE,
            });
          }
        }
      }
      if (shouldRefetchUser && !isPreviousGatherPage) {
        fetchUser();
      }

      // cleanup function that cancels pending requests
      return () => {
        source.cancel();
      };
    },
    300,
    [location, isPreviousGatherPage]
  );

  return (
    <ThemeProvider theme={theme}>
      <AppProvider>
        <EditorProvider>
          <>
            <ToastContainer
              autoClose={7000}
              transition={Slide}
              closeButton={<ToastCloseButton />}
              position={toast.POSITION.BOTTOM_RIGHT}
              draggable
            />
            <Router />
            <GlobalStyle />
          </>
        </EditorProvider>
      </AppProvider>
    </ThemeProvider>
  );
};

export default () => (
  <Provider store={store}>
    <BrowserRouter>
      <LastLocationProvider>
        <IntlProvider
          locale={defaultLangKey}
          messages={messages[defaultLangKey]}
        >
          <UserProvider>
            <App />
          </UserProvider>
        </IntlProvider>
      </LastLocationProvider>
    </BrowserRouter>
  </Provider>
);
